import styled from '@emotion/styled';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ClassCardHeader,
  getElevation,
  IconButton,
  Typography,
} from '@innovamat/glimmer-components';
import { useCourses } from '@innovamat/glow-api-client';

import { getAvatarImage } from '../../../../utils';
import { useUser } from '../../../user-management';
import { useClassroomCapabilities } from '../../hooks/use-classroom-capabilities';
import { Classroom } from '../../modules/classroom';
import { ClassCardOptions } from './class-card.options';

const SIZE = {
  S: 'S',
  M: 'M',
} as const;

type Size = (typeof SIZE)[keyof typeof SIZE];

export type ClassCardProps = {
  subtitle?: string;
  isDisabled?: boolean;
  title?: string;
  onClick?: () => void;
  onOpenOptions?: () => void;
  options?: ClassCardOptions;
  size?: Size;
  titleTooltip?: string;
  classroom: Classroom;
};

const Card = styled('div')<{ isDisabled?: boolean }>`
  ${({ isDisabled }) => isDisabled && `pointer-events: none;`}
  cursor: pointer;
  min-width: 168px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  gap: 24px;
  padding: 16px;
  background-color: ${({ theme }) =>
    theme.tokens.color.alias.cm.surface['surface-primary'].value};

  ${({ theme }) => getElevation(theme, 'elevation 2')};
  transition: box-shadow 0.1s ease-in-out;

  :hover {
    ${({ theme }) => getElevation(theme, 'elevation 4')};
  }
`;

const CardBody = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TeachersWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: calc(100% - 90px);
`;

const StudentsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const Caption = styled(Typography.Caption)`
  color: ${({ theme }) =>
    theme.tokens.color.alias.cm.text['text-subtle'].value};
`;

const Text = styled(Typography.Body3)`
  color: ${({ theme }) => theme.tokens.color.alias.cm.text.text.value};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const YearUpdateText = styled(Text)`
  color: ${({ theme }) =>
    theme.tokens.color.alias.cm.text['text-accent'].value};
  font-weight: 500;
`;

const HeaderOptions = styled.div`
  display: flex;
  gap: 8px;
  height: fit-content;
  align-items: center;
`;

const NoStudentsText = styled(Text)`
  color: ${({ theme }) => theme.tokens.color.alias.cm.text['text-error'].value};
`;

export function ClassCard({
  isDisabled,
  options,
  size = SIZE.M,
  classroom,
  subtitle,
  titleTooltip,
  title,
  onClick,
  onOpenOptions,
}: ClassCardProps): JSX.Element {
  const { t } = useTranslation();
  const { user } = useUser();

  const {
    students: { canBeManaged },
  } = useClassroomCapabilities({ classroom });

  const { getYearUpdateCourse } = useCourses({
    organizationId: user?.organizationId!,
    regionCode: user?.region!,
  });

  const isImportedFromGoogle = classroom.provider === 'google';

  const yearUpdateNextCourse = getYearUpdateCourse(classroom);

  const teachersNames = classroom.teachersNames || [];
  const numberOfStudents = classroom.numberOfStudents || 0;

  const getTeachers = (): JSX.Element => {
    const showTeachersNames =
      teachersNames?.length <= 2 && teachersNames?.length > 0;

    if (yearUpdateNextCourse && teachersNames?.length === 0) {
      return (
        <YearUpdateText>{t('classroom.updatePeriod.teachers')}</YearUpdateText>
      );
    }

    return showTeachersNames ? (
      <Text>{teachersNames.join(', ')}</Text>
    ) : (
      <Text>{teachersNames?.length}</Text>
    );
  };

  const avatar = useMemo(() => {
    return getAvatarImage({
      avatar: classroom.avatar,
      icon: classroom.icon,
      courseOrder: classroom.courseOrder!,
    });
  }, [classroom]);

  return (
    <Card onClick={onClick} isDisabled={isDisabled} data-testid="class-card">
      <ClassCardHeader
        subtitle={subtitle}
        titleTooltip={titleTooltip}
        title={title}
        avatar={avatar}
        yearUpdateNextCourse={yearUpdateNextCourse}
        rightSide={
          <HeaderOptions>
            {isImportedFromGoogle && (
              <IconButton
                size="S"
                tooltipText={t('classroom.importedFromGoogleClassroom.tooltip')}
                icon="GoogleClassroomIcon"
              />
            )}
            <ClassCardOptions onOpenOptions={onOpenOptions} options={options} />
          </HeaderOptions>
        }
      />

      {size === SIZE.M && (
        <CardBody>
          <TeachersWrapper data-testid="class-card-teachers">
            <Caption>{t('class-card.teachers')}</Caption>
            {getTeachers()}
          </TeachersWrapper>
          {canBeManaged && (
            <StudentsWrapper data-testid="class-card-students">
              <Caption>{t('class-card.students')}</Caption>
              {numberOfStudents === 0 ? (
                <NoStudentsText>
                  {t('classroom.students.pending-to-add')}
                </NoStudentsText>
              ) : (
                <Text>{numberOfStudents}</Text>
              )}
            </StudentsWrapper>
          )}
        </CardBody>
      )}
    </Card>
  );
}
